import { type FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import { ROLE_LABELS } from '../../lib/format';
import { useSessionChanged } from '../../lib/session';
import { Alert, ErrorMessage, Field, Loading, PageTitle } from '../../components/ui';
import { GoogleButton } from '../../components/GoogleButton';

interface InvitationInfo {
  email: string;
  role: string;
  expiresAt: string;
}

export function InvitationPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const sessionChanged = useSessionChanged();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationInfo>(`/api/v1/auth/invitations/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/auth/invitations/accept', { token, password, firstName, lastName, phone: phone || null });
      await sessionChanged();
      navigate('/', { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!token || invitation.isError) {
    return (
      <section className="auth-card">
        <PageTitle>Zaproszenie</PageTitle>
        <Alert>Link wygasł lub został już wykorzystany. Poproś placówkę o nowe zaproszenie.</Alert>
      </section>
    );
  }
  if (!invitation.data) return <Loading />;

  const errorCode = params.get('blad');
  return (
    <section className="auth-card">
      <PageTitle>Aktywacja konta</PageTitle>
      <p>
        Zaproszenie dla <strong>{invitation.data.email}</strong> ({ROLE_LABELS[invitation.data.role] ?? invitation.data.role}).
      </p>
      {errorCode === 'inny-email' && (
        <Alert>Konto Google ma inny adres e-mail niż zaproszenie. Użyj konta Google z adresem {invitation.data.email}.</Alert>
      )}
      <ErrorMessage error={error} />
      <GoogleButton href={`/api/v1/auth/external/google?mode=invite&token=${encodeURIComponent(token)}`} label="Aktywuj kontem Google" />
      {invitation.data.role === 'Therapist' ? (
        <p className="muted">Terapeuci logują się wyłącznie przez Google – użyj konta Google z adresem {invitation.data.email}.</p>
      ) : (
        <>
          <p className="divider">
            <span>lub ustaw hasło</span>
          </p>
          <form onSubmit={(e) => void submit(e)}>
            <Field label="Imię" name="firstName" autoComplete="given-name" value={firstName} onChange={setFirstName} required />
            <Field label="Nazwisko" name="lastName" autoComplete="family-name" value={lastName} onChange={setLastName} required />
            <Field label="Telefon (opcjonalnie)" name="phone" type="tel" autoComplete="tel" value={phone} onChange={setPhone} />
            <Field
              label="Hasło"
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={setPassword}
              hint="Co najmniej 12 znaków. Najlepiej kilka losowych słów."
              required
            />
            <button type="submit" disabled={busy}>
              Aktywuj konto
            </button>
          </form>
        </>
      )}
    </section>
  );
}
